import React, { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CertificateStatus } from 'hmi';

import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import Radio from '../../../../styles/templates/DSRadio';

import DetailsSection from '../DetailsSection';
import DetailsSubsection from '../DetailsSubsection';
import { ratingState } from '../helper';
import { getIconColour } from '../../../../helpers/app';
import StarIcon from '../../../../assets/icons/Star';
import { ExpandContent, SectionRow, useRatingStyles } from '../styles';
import { getRatingScore } from '../../../../services/DexService';
import { ApplicationInterface } from '../../../../interfaces/application';

type PropsType = {
  formHook: UseFormReturn<ApplicationInterface>;
  onClick: () => void;
  expand?: boolean;
  existingRatings?: string;
};

type RatingValues = 'A+' | 'A' | 'B' | 'C' | 'D' | '';
type RatingState = {
  storage: RatingValues;
  conditions: RatingValues;
  completeness: RatingValues;
};

const ExpandedRating: React.FC<{
  formHook: UseFormReturn<ApplicationInterface>;
  existingRatings?: string;
}> = ({ formHook, existingRatings }) => {
  const [activeSections, setActiveSections] = useState<string[]>([]);
  const [ratings, setRatings] = useState<RatingState>({
    storage: '',
    conditions: '',
    completeness: '',
  });
  const [ratingValue, setRatingValue] = useState('TBD');
  const [score, setScore] = useState(0);
  const classes = useRatingStyles();

  const { setValue, getValues, register } = formHook;

  const getScore = async (rating: string) => {
    try {
      const res = await getRatingScore(rating);

      if (res?.parsedBody) {
        setScore(res.parsedBody.score);
      }
    } catch (e) {
      // TODO Add error handling
    }
  };

  useEffect(() => {
    register('rating.rating');
  }, [register]);

  useEffect(() => {
    const { storage, conditions, completeness } = ratings;
    if (!storage || !conditions || !completeness) {
      // @ts-ignore
      setValue('rating.rating', 'TBD');
      return;
    }

    const value = `${storage}${conditions}${completeness}`;
    // @ts-ignore
    setValue('rating.rating', value);
    setRatingValue(value);
    getScore(value);
  }, [ratings, setValue]);

  useEffect(() => {
    if (!existingRatings || existingRatings === 'TBD') return;
    const values = [];

    for (let i = 0; i < existingRatings.length; i++) {
      if (existingRatings[i] === '+') continue;

      if (existingRatings[i + 1] && existingRatings[i + 1] === '+') values.push('A+');
      else values.push(existingRatings[i]);
    }

    if (existingRatings) {
      setRatings({
        storage: values[0] as RatingValues,
        conditions: values[1] as RatingValues,
        completeness: values[2] as RatingValues,
      });
    }
  }, [getValues, existingRatings]);

  const onSubsectionClick = (section: string) => {
    const index = activeSections.findIndex((s) => s === section);

    if (index > -1) setActiveSections(activeSections.filter((s) => s !== section));
    else setActiveSections([...activeSections, section]);
  };

  const onRatingClick = (key: keyof typeof ratings, value: RatingValues) => {
    setRatings({ ...ratings, [key]: value });
  };

  return (
    <ExpandContent>
      <SectionRow>
        <div className={classes.ratingHeader}>
          <Typography>
            This rating system* provides all assurance that data transactions are transparent.
            Please note that the rating you provide is required to ensure your declaration is true
            legally, technically and commercially. These criteria will determine the scoring of the
            application. It is important that these are read and understood and observed.
          </Typography>
          <div className={classes.certificate}>
            <CertificateStatus score={ratingValue} points={score} text="Certified" />
          </div>
        </div>
      </SectionRow>

      <div className={classes.ratingRow}>
        <DetailsSubsection
          title="Where is the data stored?"
          description="Access to the Personal Data Account data within the namespace of the application."
          expand={activeSections.includes('Storage')}
          onClick={() => onSubsectionClick('Storage')}
        >
          <div className={classes.ratingButtonsWrapper}>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="storage-rating"
                className={classes.ratingRadioGroup}
                value={ratings.storage}
                onChange={(e) => onRatingClick('storage', e.target.value as RatingValues)}
              >
                <FormControlLabel value="A+" control={<Radio />} label="A+" />
                <FormControlLabel value="A" control={<Radio />} label="A" />
                <FormControlLabel value="B" control={<Radio />} label="B" />
                <FormControlLabel value="C" control={<Radio />} label="C" />
                <FormControlLabel value="D" control={<Radio />} label="D" />
              </RadioGroup>
            </FormControl>

            <div className={classes.descriptionWrapper}>
              <Typography>
                The application data request to the Personal Data Account (PDA) does not require any
                data from the PDA owner. This is the case for applications that only write data into
                the PDA.
              </Typography>

              <Typography>
                The application reads data from the PDA but it does not store the data anywhere
                outside the PDA, OR that the data stored outside the PDA cannot be linked back to a
                PDA owner.
              </Typography>

              <Typography>
                The application requires data from the PDA and will store data outside the PDA but
                within its own application service. It will not transfer the data anywhere else and
                this is expressly stated under its legal terms and conditions.
              </Typography>

              <Typography>
                The application will store data outside the PDA, up to six months (or any other
                short period of time stipulated by a regulator), and may transfer the data elsewhere
                for analysis purposes. This is expressly stated under its terms and conditions.
              </Typography>

              <Typography>
                The application will store data outside the PDA and WILL transfer the data elsewhere
                for secondary purposes. This might / might not stated in the terms and conditions.
              </Typography>
            </div>
          </div>
        </DetailsSubsection>

        <DetailsSubsection
          title="What are the conditions imposed on data contributed by the application?"
          description="Reading, writing, reuse, and sharing of data."
          expand={activeSections.includes('Conditions')}
          onClick={() => onSubsectionClick('Conditions')}
        >
          <div className={classes.ratingButtonsWrapper}>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="conditions-rating"
                className={classes.ratingRadioGroup}
                value={ratings.conditions}
                onChange={(e) => onRatingClick('conditions', e.target.value as RatingValues)}
              >
                <FormControlLabel value="A+" control={<Radio />} label="A+" />
                <FormControlLabel value="A" control={<Radio />} label="A" />
                <FormControlLabel value="B" control={<Radio />} label="B" />
                <FormControlLabel value="C" control={<Radio />} label="C" />
                <FormControlLabel value="D" control={<Radio />} label="D" />
              </RadioGroup>
            </FormControl>

            <div className={classes.descriptionWrapper}>
              <Typography>
                The app does not impose any conditions on the re-use and/or re-sharing of the data
                generated by the app that has been written into the PDA.
              </Typography>

              <Typography>
                The app imposes conditions on the re-use and/or re-sharing of the data generated by
                the app for legal purposes, according to the relevant jurisdiction, e.g. protection
                of minors OR the app does not contribute any data to the PDA.
              </Typography>

              <Typography>
                The application imposes conditions on the use and/or sharing of the data generated
                by the application that has been written into the PDA. The application provides read
                only view, with restricted access via the application and no other application or
                system.
              </Typography>

              <Typography>
                The application imposes conditions on the use and/or sharing of the data generated
                by the application that has been written into the PDA. The data could be encrypted
                OR date aged for use to a fixed period and removed once not needed.
              </Typography>

              <Typography>
                The application puts conditions on the PDA data to which the user can not control
                and manage.
              </Typography>
            </div>
          </div>
        </DetailsSubsection>

        <DetailsSubsection
          title="What is the completeness of data contributed by the application?"
          description="Equality of data exchange."
          expand={activeSections.includes('Completeness')}
          onClick={() => onSubsectionClick('Completeness')}
        >
          <div className={classes.ratingButtonsWrapper}>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="completeness-rating"
                className={classes.ratingRadioGroup}
                value={ratings.completeness}
                onChange={(e) => onRatingClick('completeness', e.target.value as RatingValues)}
              >
                <FormControlLabel value="A+" control={<Radio />} label="A+" />
                <FormControlLabel value="A" control={<Radio />} label="A" />
                <FormControlLabel value="B" control={<Radio />} label="B" />
                <FormControlLabel value="C" control={<Radio />} label="C" />
                <FormControlLabel value="D" control={<Radio />} label="D" />
              </RadioGroup>
            </FormControl>

            <div className={classes.descriptionWrapper}>
              <Typography>
                The data contributed by the application into the PDA is more than personal data. All
                of the data generated by the PDA owner within the application is stored back into
                the PDA. The application also licenses other data back to the user such as the
                metadata, descriptions and images to make the data more understandable.
              </Typography>

              <Typography>
                The data written by the application into the PDA is all the data generated by the
                PDA owner within the application. The PDA does not give back all presented data to
                the application written into the PDA (within performance limits).
              </Typography>

              <Typography>
                The data written by the application into the PDA is separated. The data generated by
                the PDA owner within the application is written back into the PDA, However,
                pseudonymised data or application metadata is kept by the app but not written back
                to the PDA owner.
              </Typography>

              <Typography>
                The personal data written by the application is incomplete and separated.
                application meta data is kept by the application and not pseudonymised. The
                application and other systems / third parties would hold Personal data of the PDA
                Owner which has more data than brought into the PDA.
              </Typography>

              <Typography>
                The Personal data in the PDA can held but is not, Personal data is held out side the
                PDA and not pseudonymised. The PDA is a shell and not used.
              </Typography>
            </div>
          </div>
        </DetailsSubsection>
      </div>
    </ExpandContent>
  );
};

const Rating: React.FC<PropsType> = ({ formHook, expand, onClick, existingRatings }) => {
  const type = ratingState(formHook);
  const theme = useTheme();

  return (
    <DetailsSection
      icon={<StarIcon stroke={getIconColour(type, theme)} />}
      title="Rating"
      description="Self-reported data exchange ratings."
      expand={expand}
      onClick={onClick}
      type={type}
    >
      <ExpandedRating formHook={formHook} existingRatings={existingRatings} />
    </DetailsSection>
  );
};

export default Rating;
